/**
 * useSpeechToText Hook
 *
 * React hook for real-time speech-to-text transcription.
 * Uses Web Speech API (on-device, free) with optional Deepgram fallback.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import {
  SpeechRecognitionManager,
  DeepgramTranscription,
  TranscriptionResult,
  isSpeechRecognitionAvailable,
} from '../lib/speech/speech-recognition'

export interface UseSpeechToTextOptions {
  /** Language code (default: 'en-US') */
  language?: string
  /** Use Deepgram instead of Web Speech API */
  useDeepgram?: boolean
  /** Deepgram API key (only needed if useDeepgram is true) */
  deepgramApiKey?: string
  /** Show interim (partial) results */
  interimResults?: boolean
  /** MediaStream to transcribe (for Deepgram) */
  audioStream?: MediaStream | null
}

export interface UseSpeechToTextReturn {
  /** Start transcription */
  start: () => void
  /** Stop transcription */
  stop: () => void
  /** Current transcript (interim + final) */
  transcript: string
  /** Array of all transcription results */
  results: TranscriptionResult[]
  /** Whether transcription is active */
  isListening: boolean
  /** Whether speech recognition is available */
  isAvailable: boolean
  /** Any error that occurred */
  error: string | null
  /** Clear all transcripts */
  clearTranscript: () => void
}

/**
 * Hook for speech-to-text transcription
 *
 * @example
 * ```tsx
 * const { start, stop, transcript, isListening } = useSpeechToText()
 *
 * return (
 *   <div>
 *     <button onClick={isListening ? stop : start}>
 *       {isListening ? 'Stop' : 'Start'} Listening
 *     </button>
 *     <p>Transcript: {transcript}</p>
 *   </div>
 * )
 * ```
 */
export function useSpeechToText(options: UseSpeechToTextOptions = {}): UseSpeechToTextReturn {
  const {
    language = 'en-US',
    useDeepgram = false,
    deepgramApiKey,
    interimResults = true,
    audioStream,
  } = options

  const [transcript, setTranscript] = useState('')
  const [results, setResults] = useState<TranscriptionResult[]>([])
  const [isListening, setIsListening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const recognitionRef = useRef<SpeechRecognitionManager | null>(null)
  const deepgramRef = useRef<DeepgramTranscription | null>(null)
  const interimRef = useRef('')

  const isAvailable = isSpeechRecognitionAvailable() || !!deepgramApiKey

  // Handle transcription result
  const handleResult = useCallback((result: TranscriptionResult) => {
    if (result.isFinal) {
      setResults(prev => [...prev, result])
      setTranscript(prev => prev + (prev ? ' ' : '') + result.text)
      interimRef.current = ''
    } else if (interimResults) {
      interimRef.current = result.text
      // Update transcript with interim
      setTranscript(prev => {
        const base = results.map(r => r.text).join(' ')
        return base + (base ? ' ' : '') + result.text
      })
    }
  }, [results, interimResults])

  // Start transcription
  const start = useCallback(() => {
    if (isListening) return

    setError(null)

    if (useDeepgram && deepgramApiKey) {
      // Use Deepgram
      if (!audioStream) {
        setError('Audio stream required for Deepgram transcription')
        return
      }

      deepgramRef.current = new DeepgramTranscription(deepgramApiKey, {
        language,
        onResult: handleResult,
        onError: (err) => setError(err),
        onStart: () => setIsListening(true),
        onEnd: () => setIsListening(false),
      })

      deepgramRef.current.start(audioStream)
    } else {
      // Use Web Speech API (on-device)
      if (!isSpeechRecognitionAvailable()) {
        setError('Speech recognition not available in this browser')
        return
      }

      recognitionRef.current = new SpeechRecognitionManager({
        language,
        continuous: true,
        interimResults,
        onResult: handleResult,
        onError: (err) => setError(err),
        onStart: () => setIsListening(true),
        onEnd: () => setIsListening(false),
      })

      recognitionRef.current.start()
    }
  }, [isListening, useDeepgram, deepgramApiKey, audioStream, language, interimResults, handleResult])

  // Stop transcription
  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop()
      recognitionRef.current = null
    }

    if (deepgramRef.current) {
      deepgramRef.current.stop()
      deepgramRef.current = null
    }

    setIsListening(false)
  }, [])

  // Clear transcripts
  const clearTranscript = useCallback(() => {
    setTranscript('')
    setResults([])
    interimRef.current = ''

    if (recognitionRef.current) {
      recognitionRef.current.clearTranscripts()
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop()
    }
  }, [stop])

  return {
    start,
    stop,
    transcript,
    results,
    isListening,
    isAvailable,
    error,
    clearTranscript,
  }
}

/**
 * Hook for transcribing a voice call
 *
 * @example
 * ```tsx
 * const { startTranscription, transcript } = useCallTranscription({
 *   localStream,
 *   remoteStream,
 * })
 * ```
 */
export function useCallTranscription(options: {
  localStream?: MediaStream | null
  remoteStream?: MediaStream | null
  language?: string
}) {
  const { localStream, remoteStream, language = 'en-US' } = options

  const [localTranscript, setLocalTranscript] = useState('')
  const [remoteTranscript, setRemoteTranscript] = useState('')

  const localRecognition = useRef<SpeechRecognitionManager | null>(null)
  const [isTranscribing, setIsTranscribing] = useState(false)

  const startTranscription = useCallback(() => {
    if (!isSpeechRecognitionAvailable()) {
      console.error('Speech recognition not available')
      return false
    }

    // For now, we can only transcribe the microphone input
    // Remote audio transcription requires more complex setup
    localRecognition.current = new SpeechRecognitionManager({
      language,
      continuous: true,
      interimResults: true,
      onResult: (result) => {
        if (result.isFinal) {
          setLocalTranscript(prev => prev + (prev ? ' ' : '') + result.text)
        }
      },
    })

    localRecognition.current.start()
    setIsTranscribing(true)
    return true
  }, [language])

  const stopTranscription = useCallback(() => {
    if (localRecognition.current) {
      localRecognition.current.stop()
      localRecognition.current = null
    }
    setIsTranscribing(false)
  }, [])

  const clearTranscripts = useCallback(() => {
    setLocalTranscript('')
    setRemoteTranscript('')
  }, [])

  // Cleanup
  useEffect(() => {
    return () => {
      stopTranscription()
    }
  }, [stopTranscription])

  return {
    startTranscription,
    stopTranscription,
    localTranscript,
    remoteTranscript,
    isTranscribing,
    clearTranscripts,
    fullTranscript: [localTranscript, remoteTranscript].filter(Boolean).join('\n'),
  }
}
